import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { finalize, Subject, takeUntil } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { ICity } from '@gray/domain/models/location/city.model';
import { ConfirmedValidator } from '@gray/domain/validators/password-confirmed.validator';
import { CoachProfileService } from '../../core/services/coach-profile.service';
import { LocationService } from '../../core/services/location.service';
import { CoachStateService } from '../../core/services/coach-state.service';
import { CoachAuthService } from '../../core/services/coach-auth.service';
import { CoachGoogleService, ICoachGoogleStatus } from '../../core/services/coach-google.service';
import { ICoach } from '../../core/models/coach.model';

@Component({
  selector: 'coach-account',
  templateUrl: './account.component.html',
  styleUrl: './account.component.scss',
})
export class AccountComponent implements OnInit, OnDestroy {

  fg: FormGroup = this.formBuilder.group({
    specialization: [null, [Validators.maxLength(255)]],
    bio: [null],
    education: [null, [Validators.maxLength(255)]],
    university: [null, [Validators.maxLength(255)]],
    department: [null, [Validators.maxLength(255)]],
    graduationYear: [null, [Validators.min(1950), Validators.max(2100)]],
    experienceYears: [null, [Validators.min(0), Validators.max(80)]],
    hometownCityId: [null],
    cityId: [null],
    gender: [null],
    birthDate: [null],
  });

  fgPassword: FormGroup = this.formBuilder.group({
    currentPassword: [null, [Validators.required, Validators.minLength(6), Validators.maxLength(50)]],
    password: [null, [Validators.required, Validators.minLength(6), Validators.maxLength(50)]],
    rePassword: [null, [Validators.required, Validators.minLength(6), Validators.maxLength(50)]],
  }, {
    validators: [
      ConfirmedValidator('password', 'rePassword'),
    ]
  });

  coach?: ICoach;
  cities: ICity[] = [];
  genderKeys = ['account.gender_male', 'account.gender_female', 'account.gender_unspecified'];
  genders: { id: number, name: string }[] = [];
  birthMaxDate = new Date();

  googleStatus?: ICoachGoogleStatus;
  googleIsLoading = false;
  googleIsConnecting = false;

  isLoading = false;
  isSubmitted = false;
  passwordIsLoading = false;
  passwordIsSubmitted = false;
  passwordErrKey?: string;

  destroyed$ = new Subject();

  constructor(
    private formBuilder: FormBuilder,
    private profileService: CoachProfileService,
    private locationService: LocationService,
    private coachState: CoachStateService,
    private coachAuthService: CoachAuthService,
    private googleService: CoachGoogleService,
    private route: ActivatedRoute,
    private toastrService: ToastrService,
    private translate: TranslateService,
  ) {
  }

  ngOnInit(): void {
    this.getGenders();
    this.getCities();
    this.getProfile();
    this.getGoogleStatus();
    this.reportConnectResult();
  }

  // region Google account
  getGoogleStatus() {
    this.googleIsLoading = true;
    this.googleService.getStatus()
      .pipe(takeUntil(this.destroyed$), finalize(() => this.googleIsLoading = false))
      .subscribe(r => {
        this.googleStatus = r.data as ICoachGoogleStatus;
      });
  }

  /** Google sends the browser back here with the outcome of the consent screen. */
  reportConnectResult() {
    const status = this.route.snapshot.queryParamMap.get('status');
    if (!status) {
      return;
    }

    if (status === 'connected') {
      this.toastrService.success(this.translate.instant('account.google.connected_toast'));
    } else {
      this.toastrService.error(this.translate.instant('account.google.error_toast'));
    }
  }

  /** Leaves the panel for Google's consent screen; the callback brings the coach back. */
  connectGoogle() {
    this.googleIsConnecting = true;
    this.googleService.getConnectUrl()
      .pipe(takeUntil(this.destroyed$), finalize(() => this.googleIsConnecting = false))
      .subscribe(r => {
        const url = r.data?.url;
        if (url) {
          window.location.href = url;
        }
      });
  }

  async confirmDisconnectGoogle() {
    const Swal = (await import('sweetalert2')).default;

    const result = await Swal.fire({
      text: this.translate.instant('account.google.disconnect_confirm'),
      showCancelButton: true,
      confirmButtonText: this.translate.instant('buttons.yes'),
      cancelButtonText: this.translate.instant('buttons.cancel_l'),
      heightAuto: false,
    });

    if (result.isConfirmed) {
      this.disconnectGoogle();
    }
  }

  disconnectGoogle() {
    this.googleIsLoading = true;
    this.googleService.disconnect()
      .pipe(takeUntil(this.destroyed$), finalize(() => this.googleIsLoading = false))
      .subscribe(() => {
        this.toastrService.success(this.translate.instant('account.google.disconnected_toast'));
        this.getGoogleStatus();
      });
  }
  // endregion

  getGenders() {
    this.translate.get(this.genderKeys)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(r => {
        this.genders = this.genderKeys.map((key, index) => ({id: index + 1, name: r[key]}));
      });
  }

  getCities() {
    this.locationService.getCities()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(r => {
        this.cities = r.data;
      });
  }

  getProfile() {
    this.isLoading = true;
    this.profileService.getProfile()
      .pipe(takeUntil(this.destroyed$), finalize(() => this.isLoading = false))
      .subscribe(r => {
        this.setCoach(r.data as ICoach);
      });
  }

  searchFn(term: string, item: ICity) {
    const normalizeString = (str: string) =>
      str.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

    return (normalizeString(item.name).indexOf(normalizeString(term)) > -1);
  }

  onSubmit() {
    this.isSubmitted = true;
    if (this.fg.invalid) {
      return;
    }

    this.isLoading = true;
    this.profileService.updateProfile(this.profileData())
      .pipe(takeUntil(this.destroyed$), finalize(() => this.isLoading = false))
      .subscribe(r => {
        this.setCoach(r.data as ICoach);
        this.isSubmitted = false;
        this.toastrService.success(this.translate.instant('account.update_success'));
      });
  }

  onPasswordSubmit() {
    this.passwordIsSubmitted = true;
    this.passwordErrKey = undefined;

    if (this.fgPassword.invalid) {
      return;
    }

    this.passwordIsLoading = true;
    const formVal = this.fgPassword.value;

    this.coachAuthService.changePassword({
      currentPassword: formVal.currentPassword,
      password: formVal.password,
    })
      .pipe(takeUntil(this.destroyed$), finalize(() => this.passwordIsLoading = false))
      .subscribe(() => {
        this.passwordIsSubmitted = false;
        this.fgPassword.patchValue({
          currentPassword: null,
          password: null,
          rePassword: null,
        });
        this.toastrService.success(this.translate.instant('account.password_update_success'));
      }, () => {
        // The API answers a wrong current password with a plain 400.
        this.passwordErrKey = 'account.current_password_wrong';
      });
  }

  private setCoach(coach: ICoach) {
    this.coach = coach;
    this.coachState.setCurrentCoach(coach);

    const profile = coach.coachProfile;
    if (!profile) {
      return;
    }

    const birthDate = profile.birthDate ? new Date(profile.birthDate) : null;

    this.fg.patchValue({
      specialization: profile.specialization,
      bio: profile.bio,
      education: profile.education,
      university: profile.university,
      department: profile.department,
      graduationYear: profile.graduationYear,
      experienceYears: profile.experienceYears,
      hometownCityId: profile.hometownCityId,
      cityId: profile.cityId,
      gender: profile.gender,
      birthDate: birthDate ? {
        year: birthDate.getFullYear(),
        month: birthDate.getMonth() + 1,
        day: birthDate.getDate(),
      } : null,
    });
  }

  /** The API expects an ISO date and plain numbers, so the form value is mapped before sending. */
  private profileData() {
    const formVal = this.fg.value;
    const birthDate = formVal.birthDate;

    return {
      specialization: formVal.specialization,
      bio: formVal.bio,
      education: formVal.education,
      university: formVal.university,
      department: formVal.department,
      graduationYear: this.toNumber(formVal.graduationYear),
      experienceYears: this.toNumber(formVal.experienceYears),
      hometownCityId: formVal.hometownCityId,
      cityId: formVal.cityId,
      gender: formVal.gender,
      birthDate: birthDate
        ? `${birthDate.year}-${String(birthDate.month).padStart(2, '0')}-${String(birthDate.day).padStart(2, '0')}`
        : null,
    };
  }

  private toNumber(value: any) {
    return (value === null || value === '' || typeof value === 'undefined') ? null : Number(value);
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
